import { memo } from 'preact/compat';
import { useRef, useCallback } from 'preact/hooks';
import { useReorderableItem } from '../hooks/PRIVATE_useReorderable';
import { ReorderableItemProps } from '../UNSAFE_Collection/Reorderable.types';
import { ITEM_SELECTOR } from '../utils/PRIVATE_collectionUtils';
import { classNames } from '../utils/UNSAFE_classNames';
import { styles } from './themes/ListViewStyles.css';

export type ListReorderableItemProps<K> = ReorderableItemProps<K> & {
  currentKey: K | undefined;
};

/**
 * A private item that will be used by ReorderableCardFlexView, location TBD (could be moved to PRIVATE_CardFlexView)
 */
export function ListViewReorderableItem<K extends string | number>({
  currentKey,
  children,
  dragKey,
  setDragKey,
  onReorder,
  rootRef,
  itemKey
}: ListReorderableItemProps<K> & { itemKey: K }) {
  const itemRef = useRef<HTMLDivElement | null>(null);
  const itemStyles = {
    itemOverlayFront: styles.reorderableItemOverlayFront,
    itemOverlayRear: styles.reorderableItemOverlayRear,
    itemDragger: styles.reorderableItemDragger
  };
  const getItem = () => itemRef?.current?.closest(ITEM_SELECTOR);
  const setTransferData = useCallback(
    (event: DragEvent) => {
      event.dataTransfer?.setData('text/listview-items-key', JSON.stringify(itemKey));
    },
    [itemKey]
  );
  const { rootProps, isDragged, overlayZoneBeforeProps, overlayZoneAfterProps } =
    useReorderableItem({
      currentKey,
      isDisabled: onReorder ? false : true,
      draggable: onReorder ? true : false,
      dragKey,
      setDragKey,
      onReorder,
      rootRef,
      ref: itemRef,
      getItem,
      setTransferData,
      itemStyles,
      itemSelector: ITEM_SELECTOR,
      isVertical: true
    });
  const classes = classNames([rootProps?.class, styles.reorderableItemDnd]);

  return (
    <div {...rootProps} draggable={false} class={classes}>
      {children}
      {!isDragged && dragKey && (
        <>
          <div {...overlayZoneBeforeProps}></div>
          <div {...overlayZoneAfterProps}></div>
        </>
      )}
    </div>
  );
}

export const MemoizeListViewReorderableItem = memo(
  ListViewReorderableItem,
  <K,>(prev: ListReorderableItemProps<K>, next: ListReorderableItemProps<K>) => {
    if (prev && next) {
      return (
        prev?.children === next?.children &&
        prev?.currentKey === next?.currentKey &&
        prev?.dragKey === next?.dragKey
      );
    }
    return false;
  }
);
